<template>
  <div>
    <base-modal
      id="baseModal"
      :show="baseModal"
      size="modal-md"
      centered
      padding-md
      :show-footer="false"
      @closed="onClosed"
    >
      <div v-click-outside="onClosed">
        <!-- Mode Default -->
        <div v-show="mode === MODES.DEFAULT">
          <base-heading class="mb-0 heading-title">
            Schedule send
          </base-heading>
          <hr>
          <base-text
            light
            class="schedule-email__description mt-4"
          >
            Pacific Daylight Times
          </base-text>

          <div
            v-for="(schedule, index) in schedules"
            :key="index"
            class="schedule-email__cell"
            @click="scheduleSendEmail(schedule.value)"
          >
            <div class="schedule-email__cell__label">
              {{ schedule.label }}
            </div>
            <div class="schedule-email__cell__value">
              {{ formatHuman(schedule.value) }}
            </div>
          </div>
          <div class="schedule-email__pick py-4">
            <base-link-with-icon
              icon="pick-date"
              size="m"
              @click="openTimepickerModal"
            >
              Pick date & time
            </base-link-with-icon>
          </div>
        </div>
        <div v-show="mode === MODES.CUSTOM">
          <base-heading class="mb-0 heading-title">
            Pick date & time
          </base-heading>
          <hr>
          <div class="schedule-email__custom">
            <!-- Left col -->
            <div>
              <v-date-picker
                v-model="selectedDate"
                class="schedule-email__custom__picker"
                :min-date="new Date()"
              />
            </div>
            <!-- Right col -->
            <div class="schedule-email__custom__detail">
              <base-text
                light
                size="s"
                class="schedule-email__custom__date-selected"
                style="max-width: 146px; color: #909090;"
              >
                {{ selectedDate || '--' | nicedate('small') }}
              </base-text>
              <base-selector
                ref="timeSelector"
                v-click-outside="closeTimeSelector"
                class="schedule-email__custom__date-time"
                :items="times"
                :value="selectedTime"
                :scroll-option-index="timeIndexOf('08:00AM')"
                width="146"
                height="200"
                @input="onTimeChange"
              />
            </div>
          </div>
          <div class="d-flex bottom-btn" style="justify-content: right;">
            <base-button
              inverse
              style="min-width: 127px;"
              class="mx-0 sch-send"
              @click="scheduleSendEmail(selectedDateTime)"
            >
              Schedule Send
            </base-button>
          </div>
        </div>
        <!-- Mode Default -->
      </div>
    </base-modal>
  </div>
</template>

<script>
/**
 * This component Shows a modal an allows to pick a date to schedule an Email send. When closed, it resets to default state.
*/

import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import whichbrowser from '@/components/mixins/whichbrowser';

const MODES = {
  DEFAULT: 0,
  CUSTOM: 1,
};
export default {
  name: 'ScheduleEmailModal',
  components: {
    VDatePicker,
  },
  mixins: [whichbrowser],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Number,
      required: true,
    },
  },
  /**
   * @description
   * @returns {any}
   */
  data() {
    this.MODES = MODES;

    return {
      mode: MODES.DEFAULT, // 0: Default | 1: Custom
      baseModal: false,
      selectedDate: null,
      selectedTime: { name: '' },
    };
  },
  computed: {
    /**
     * @description
     * @returns {any}
     */
    now() {
      return this.$moment().startOf('day');
    },
    /**
     * @description
     * @returns {any}
     */
    tomorrow() {
      return this.now.clone().add(1, 'days');
    },
    /**
     * @description
     * @returns {any}
     */
    tomorrowMorning() {
      return this.tomorrow.clone().hour(8);
    },
    /**
     * @description
     * @returns {any}
     */
    tomorrowAfternoon() {
      return this.tomorrow.clone().hour(13);
    },
    /**
     * @description
     * @returns {any}
     */
    mondayMorning() {
      return this.now.clone().add(1, 'weeks').isoWeekday(1).hour(8);
    },
    /**
     * @description
     * @returns {any}
     */
    schedules() {
      return [
        { label: 'Tomorrow morning', value: this.tomorrowMorning },
        { label: 'Tomorrow afternoon', value: this.tomorrowAfternoon },
        { label: 'Monday morning', value: this.mondayMorning },
      ];
    },
    /**
     * @description
     * @returns {any}
     */
    selectedDateTime() {
      return this.parseDate(this.selectedDate, this.selectedTime.name);
    },
    /**
     * @description
     * @returns {any}
     */
    times() {
      const hourStrings = [];
      const indexes = [...Array(24).keys()];
      const dayIsToday = this.selectedDate ? this.$moment(this.selectedDate).isSame(new Date(), 'day') : false;
      const dayIsInPast = this.selectedDate ? this.$moment(this.selectedDate).isBefore() : false;
      const shouldCheckDisablity = dayIsToday || dayIsInPast;

      indexes.forEach((index) => {
        const hour = index;
        [...Array(4).keys()].forEach((minutes) => {
          const time = this.$moment({ hour, minute: 15 * minutes });
          const disabled = shouldCheckDisablity ? time.isBefore(this.$moment().add(15, 'minutes')) : false;
          hourStrings.push({ name: time.format('hh:mmA'), disabled });
        });
      });
      return hourStrings;
    },
  },
  watch: {
    /**
     * @description
     * @param {any} value
     */
    show(value) {
      this.baseModal = value;
    },
  },
  methods: {
    /**
     * @description
     */
    closeTimeSelector() {
      this.$nextTick().then(() => {
        const el = this.$refs.timeSelector;
        if (el.selector) el.closeSelector();
      });
    },
    /**
     * @description
     * @param {any} date
     * @param {any} time
     * @returns {any}
     */
    parseDate(date, time) {
      let hour = time.substring(0, 2).replace(':', '');
      const minutes = time.substring(3, 5);
      const initials = time.substring(5, 7);
      if (initials === 'PM') {
        if (hour !== '12') {
          hour = parseInt(hour, 10) + 12;
        }
      }
      const newDate = this.$moment(date.toISOString());
      newDate.startOf('day').hour(hour).minutes(minutes);
      return newDate;
    },
    /**
     * @description
     * @param {any} momentObject
     * @returns {any}
     */
    formatHuman(momentObject) {
      return momentObject.format('MMMM Do[,] h:mm A');
    },
    /**
     * @description
     */
    openTimepickerModal() {
      this.mode = MODES.CUSTOM;
    },
    /**
     * @description
     * @param {any} scheduled
     */
    scheduleSendEmail(scheduled) {
      this.$emit('schedule-email', scheduled);
    },
    /**
     * @description
     */
    onClosed() {
      this.$emit('close-schedule-modal');
      this.mode = MODES.DEFAULT;
      this.selectedDate = null;
      this.selectedTime = { name: '' };
    },
    /**
     * @description
     * @param {any} newval
     */
    onTimeChange(newval) {
      this.selectedTime = newval;
    },
    timeIndexOf(time) {
      return this.times.findIndex((el) => el.name === time);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-body--padding-md{
  padding-top:17px !important;
  padding-left:25px !important;
  padding-right:25px !important;
}
.schedule-email{
  &__description{
    color: #767676;
    margin-bottom: 10px;
    font-family: 'SilkaLight';
    font-size: 14px;
  }
  &__pick{
    min-height: 50px;
    padding-left: 8px;
    margin-top: 18px;
    display: flex;
    align-items: center;
  }
  &__cell {
    padding-left: 7px;
    padding-right: 7px;
    display: grid;
    grid-template-columns: repeat(2, 50%);
    min-height: 32px;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s;
    font-family: 'SilkaLight';
    font-style: normal;
    font-size: 14px;
    line-height: 135%;

    &:hover{
      background: #F0F0F0;
    }

    &__value{
      text-align: right;
      // color: $gray;
    }
  }

  // Custom date
  &__custom{
    display: grid;
    column-gap: 15px;
    grid-template-columns: 285px 146px;
    margin-bottom: 30px;

    &__detail{
      display: grid;
      grid-template-rows: 35px auto 35px;
      row-gap: 26px;
    }

    &__picker{
      ::v-deep .vc-pane-container {
        min-width: 285px;
        width: 100%;
      }
      ::v-deep .vc-header {
        padding-bottom: 13px;
      }
      ::v-deep .vc-weekday {
        font-family: 'SilkaMedium';
        font-size: 10px;
        color: #C6C6C6;

      }
      ::v-deep .vc-highlight{
        background-color: black !important;
      }
      ::v-deep .vc-day{
      font-family: 'SilkaLight';
      font-size: 14px;
      color: #909090;
      }
      ::v-deep .vc-title{
        font-family: 'SilkaMedium';
        font-weight: 500;
        font-size: 14px;
        color: #1B1B1B;
      }
      ::v-deep .vc-svg-icon{
        margin-top: 3px;
        height: 19px;
        color: black;
      }
    }

    &__date-selected{
      font-family: 'SilkaLight';
      font-size: 14px;
      border: 1px solid $gray-light-4;
      border-radius: 5px;
      padding-left: 1rem;
      padding-right: 1rem;
      height: 44px;
      display: flex;
      align-items: center;
    }

    &__date-time {
      ::v-deep.selector{
        height: 44px;
        padding-left: 1rem !important;
        display: flex;
        align-items: center;
        border-radius: 5px;
        color: #909090;
      }
      ::v-deep.base-text{
        font-size: 14px;
      }
    }
  }
}
.heading-title {
  font-family: 'Tiempos Headline';
  font-weight: 600;
  font-size: 19px;
  padding-bottom: 5px;
}
.sch-send {
  font-family: 'SilkaLight';
  font-size: 14px;
}
.bottom-btn{
  margin-bottom: 16px;
  ::v-deep.base-button{
    padding: 5px 10px !important;
  }
}
</style>
